<template>
<div class="message-pattern-textarea-wrapper">
    <!-- Standard v-textarea that handles newlines properly -->
    <v-textarea
        ref="textarea"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        rows="1"
        auto-grow
        hide-details
        outlined
        class="pattern-textarea"
        @input="onInput"
        @keydown="onKeyDown"
        @focus="onFocus"
        @blur="onBlur"
    />

    <!-- Autocomplete dropdown menu -->
    <v-menu
        v-model="show_autocomplete"
        :position-x="menu_x"
        :position-y="menu_y"
        absolute
        :close-on-content-click="false"
        :close-on-click="true"
        max-height="250"
        max-width="350"
    >
        <v-list dense class="autocomplete-menu">
            <v-list-item
                v-for="(variable, variable_index) in filtered_variables"
                :key="variable.name"
                :class="{ 'highlighted': variable_index === highlighted_index }"
                @click="insertVariable(variable.name)"
                @mouseenter="highlighted_index = variable_index"
            >
                <v-list-item-content>
                    <v-list-item-title class="var-name">
                        {{ "{" + variable.name + "}" }}
                    </v-list-item-title>
                    <v-tooltip bottom max-width="320">
                        <template v-slot:activator="{ on, attrs }">
                            <v-list-item-subtitle
                                class="var-description"
                                v-bind="attrs"
                                v-on="on"
                            >
                                {{ variable.description }}
                            </v-list-item-subtitle>
                        </template>
                        <span>{{ variable.description }}</span>
                    </v-tooltip>
                </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="filtered_variables.length === 0">
                <v-list-item-content>
                    <v-list-item-subtitle>No matching variables</v-list-item-subtitle>
                </v-list-item-content>
            </v-list-item>
        </v-list>
    </v-menu>
</div>
</template>

<script>
const mirror_style_props = [
    'font-family', 'font-size', 'font-weight', 'font-style',
    'letter-spacing', 'text-transform', 'word-spacing',
    'text-indent', 'white-space', 'word-wrap', 'word-break',
    'padding-left', 'padding-right', 'padding-top', 'padding-bottom',
    'border-left-width', 'border-right-width', 'border-top-width', 'border-bottom-width',
    'line-height', 'box-sizing'
];

export default {
    name: 'MessagePatternTextarea',

    props: {
        value: {
            type: String,
            default: ''
        },
        variables: {
            type: Array,
            default: () => []
        },
        placeholder: {
            type: String,
            default: 'Enter message pattern...'
        },
        disabled: {
            type: Boolean,
            default: false
        },

    },

    data() {
        return {
            is_focused: false,
            show_autocomplete: false,
            autocomplete_query: '',
            highlighted_index: 0,
            menu_x: 0,
            menu_y: 0,
            saved_cursor_position: null, // Save cursor position for chip insertion
        };
    },

    computed: {
        filtered_variables() {
            if (!this.autocomplete_query) {
                return this.variables;
            }
            const query = this.autocomplete_query.toLowerCase();
            return this.variables.filter(v =>
                v.name.toLowerCase().includes(query)
            );
        }
    },

    watch: {
        filtered_variables() {
            this.highlighted_index = 0;
        }
    },

    methods: {
        onInput(new_value) {
            this.$emit('input', new_value);

            // Check for autocomplete trigger
            this.$nextTick(() => {
                this.checkAutocomplete(new_value);
            });
        },

        checkAutocomplete(text) {
            const textarea = this.$refs.textarea?.$refs?.input;
            if (!textarea) return;

            const cursor_pos = textarea.selectionStart;
            const text_before_cursor = text.substring(0, cursor_pos);

            // Match pattern: { followed by optional letters/hyphens (not closed)
            const match = text_before_cursor.match(/\{([a-zA-Z-]*)$/);

            if (match) {
                this.autocomplete_query = match[1];
                this.highlighted_index = 0;
                this.positionAutocompleteMenu(textarea);
                this.show_autocomplete = true;
            } else {
                this.show_autocomplete = false;
                this.autocomplete_query = '';
            }
        },

        positionAutocompleteMenu(textarea) {
            if (!textarea) return;

            // Get cursor position coordinates using a mirror div
            const cursor_coords = this.getCursorCoordinates(textarea);
            
            this.menu_x = cursor_coords.x;
            this.menu_y = cursor_coords.y + 20; // Offset below the cursor line
        },

        /**
         * Calculate the pixel coordinates of the cursor in the textarea
         * Uses a hidden mirror div to measure text dimensions
         */
        getCursorCoordinates(textarea) {
            const cursor_pos = textarea.selectionStart;
            const text = textarea.value.substring(0, cursor_pos);

            // Create a mirror div with same styling as textarea
            const mirror = document.createElement('div');
            const computed_style = window.getComputedStyle(textarea);
            const mirror_style = mirror.style;

            mirror_style.position = 'absolute';
            mirror_style.visibility = 'hidden';
            mirror_style.whiteSpace = 'pre-wrap';
            mirror_style.width = computed_style.width;
            
            mirror_style_props.forEach((style_name) => {
                mirror_style.setProperty(style_name, computed_style.getPropertyValue(style_name));
            });

            // Put text content up to cursor, with a span at the end to measure position
            mirror.textContent = text;
            const marker = document.createElement('span');
            marker.textContent = '|';
            mirror.appendChild(marker);

            document.body.appendChild(mirror);

            // Get marker position relative to the textarea
            const textarea_rect = textarea.getBoundingClientRect();
            const marker_rect = marker.getBoundingClientRect();
            const mirror_rect = mirror.getBoundingClientRect();

            // Calculate position relative to viewport
            const x = textarea_rect.left + (marker_rect.left - mirror_rect.left) - textarea.scrollLeft;
            const y = textarea_rect.top + (marker_rect.top - mirror_rect.top) - textarea.scrollTop;

            // Clean up
            mirror.remove();

            return { x, y };
        },

        onKeyDown(event) {
            // Only intercept keys when autocomplete is showing with results
            if (this.show_autocomplete && this.filtered_variables.length > 0) {
                switch (event.key) {
                    case 'ArrowDown':
                        event.preventDefault();
                        this.highlighted_index = Math.min(
                            this.highlighted_index + 1,
                            this.filtered_variables.length - 1
                        );
                        return;
                    case 'ArrowUp':
                        event.preventDefault();
                        this.highlighted_index = Math.max(this.highlighted_index - 1, 0);
                        return;
                    case 'Enter':
                    case 'Tab':
                        event.preventDefault();
                        this.insertVariable(this.filtered_variables[this.highlighted_index].name);
                        return;
                    case 'Escape':
                        event.preventDefault();
                        this.show_autocomplete = false;
                        return;
                }
            }

            // Close autocomplete on Escape even if no results
            if (event.key === 'Escape' && this.show_autocomplete) {
                event.preventDefault();
                this.show_autocomplete = false;
            }
            
            // Let all other keys (including Enter for newlines) pass through normally
        },

        /**
         * Core insertion method - inserts content at the saved cursor position
         * @param {string} content - The content to insert
         * @param {Object} options - Configuration options
         * @param {boolean} options.add_space_after - Whether to add space after content if needed (default: true)
         */
        insertAtCursor(content, options = {}) {
            const { add_space_after = true } = options;
            const text = this.value || '';
            
            // Use saved cursor position if available, otherwise insert at end
            let insert_pos = this.saved_cursor_position;
            if (insert_pos === null || insert_pos === undefined || insert_pos > text.length) {
                insert_pos = text.length;
            }
            
            // Add space separators if needed
            const text_before = text.substring(0, insert_pos);
            const text_after = text.substring(insert_pos);
            const need_space_before = text_before.length > 0 && !text_before.endsWith(' ') && !text_before.endsWith('\n');
            const need_space_after = add_space_after && text_after.length > 0 && !text_after.startsWith(' ') && !text_after.startsWith('\n');
            
            const insertion = (need_space_before ? ' ' : '') + content + (need_space_after ? ' ' : '');
            const new_text = text_before + insertion + text_after;
            const new_cursor_pos = insert_pos + insertion.length;

            this.$emit('input', new_text);

            this.$nextTick(() => {
                const textarea = this.$refs.textarea?.$refs?.input;
                if (textarea) {
                    textarea.focus();
                    textarea.setSelectionRange(new_cursor_pos, new_cursor_pos);
                    this.saved_cursor_position = new_cursor_pos;
                }
            });
        },

        /**
         * Insert a variable from autocomplete - replaces the {query} trigger text
         */
        insertVariable(var_name) {
            const textarea = this.$refs.textarea?.$refs?.input;
            if (!textarea) return;

            const text = this.value || '';
            const cursor_pos = textarea.selectionStart;
            const text_before_cursor = text.substring(0, cursor_pos);

            // Find the opening brace position to replace
            const brace_pos = text_before_cursor.lastIndexOf('{');
            if (brace_pos === -1) return;

            // Build new text: before brace + variable + after cursor
            const new_text = text.substring(0, brace_pos) +
                            '{' + var_name + '}' +
                            text.substring(cursor_pos);

            this.show_autocomplete = false;
            this.autocomplete_query = '';

            this.$emit('input', new_text);

            // Position cursor after the inserted variable
            this.$nextTick(() => {
                const new_cursor_pos = brace_pos + var_name.length + 2;
                textarea.setSelectionRange(new_cursor_pos, new_cursor_pos);
                textarea.focus();
                this.saved_cursor_position = new_cursor_pos;
            });
        },

        /**
         * Insert a variable at the saved cursor position (from chip click)
         */
        insertVariableAtEnd(var_name) {
            this.insertAtCursor('{' + var_name + '}');
        },

        /**
         * Insert an emoji at the saved cursor position
         */
        insertEmojiAtCursor(emoji) {
            this.insertAtCursor(emoji, { add_space_after: false });
        },

        onFocus() {
            this.is_focused = true;
            this.$emit('focus');
        },

        onBlur() {
            this.is_focused = false;
            
            // Save cursor position before blur for chip insertion
            const textarea = this.$refs.textarea?.$refs?.input;
            if (textarea) {
                this.saved_cursor_position = textarea.selectionStart;
            }
            
            this.$emit('blur');
            setTimeout(() => {
                if (!this.is_focused) {
                    this.show_autocomplete = false;
                }
            }, 200);
        },

        focus() {
            this.$refs.textarea?.$refs?.input?.focus();
        },

        getPlainText() {
            return this.value || '';
        }
    }
};
</script>

<style lang="scss" scoped>
.message-pattern-textarea-wrapper {
    width: 100%;
}

.pattern-textarea {
    flex: 1;
}

// Autocomplete menu styles
.autocomplete-menu {
    max-height: 250px;
    overflow-y: auto;
}

.v-list-item {
    &.highlighted {
        background-color: rgba(0, 0, 0, 0.08);
    }
}

.var-name {
    font-family: 'Roboto Mono', monospace;
    color: var(--v-primary-base, #1976d2);
    font-weight: 500;
}

.var-description {
    font-size: 0.85em;
    color: rgba(0, 0, 0, 0.6);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

</style>
